import { format, parseISO } from 'date-fns';

import { FieldRow, FieldValue } from '~/components/common/detailFields';
import { HeaderField } from '~/components/common/HeaderField';
import { formatLastCat5Test } from '~/components/compliance/formatters';
import { ELEVATOR_TYPE_LABELS } from '~/components/portfolio/propertyCarColumns';
import type { Car, ElevatorTypeEnum } from '~/data/generated/model';
import { formatNumber } from '~/utils/format';

/**
 * An unlabelled elevator type is possible — compliance carries `BlankEnum`'s
 * `''` on top of the car's own union — so anything not in the map falls back to
 * no label rather than indexing it with something that isn't there.
 */
function elevatorTypeLabel(type: string | null | undefined): string | null {
  if (!type || !(type in ELEVATOR_TYPE_LABELS)) return null;
  return ELEVATOR_TYPE_LABELS[type as ElevatorTypeEnum];
}

const displayDate = (value: string | null | undefined) =>
  value ? format(parseISO(value), 'MMM d, yyyy') : null;

/**
 * The unit record — the Edit Unit form's fields, read back, in the same order and from
 * the same `carsRetrieve` read, so what you see is what Edit opens. From the car itself
 * rather than the property's KPI list, which is scoped to the selected period and omits
 * a car with no tickets in it.
 *
 * Name, licence and critical-unit are the title, the line under it and a chip a few
 * lines up, so they are not repeated here.
 */
export function ElevatorUnitFields({ car, loading }: { car: Car | undefined; loading: boolean }) {
  const loaded = !loading && car !== undefined;

  return (
    <FieldRow>
      <HeaderField label="Device type">
        <FieldValue loaded={loaded} value={elevatorTypeLabel(car?.elevatorType)} />
      </HeaderField>

      <HeaderField label="Installation year">
        <FieldValue loaded={loaded} value={car?.installationYear} />
      </HeaderField>

      <HeaderField label="Modernization year">
        <FieldValue loaded={loaded} value={car?.modernizationYear} />
      </HeaderField>

      <HeaderField label="Landings">
        <FieldValue loaded={loaded} value={car?.numLandings} />
      </HeaderField>

      <HeaderField label="Entrances">
        <FieldValue loaded={loaded} value={car?.numEntrances} />
      </HeaderField>

      <HeaderField label="Speed">
        <FieldValue
          loaded={loaded}
          value={car?.speedFpm != null ? `${formatNumber(car.speedFpm)} fpm` : null}
        />
      </HeaderField>

      <HeaderField label="Capacity">
        <FieldValue
          loaded={loaded}
          value={car?.capacityLbs != null ? `${formatNumber(car.capacityLbs)} lbs` : null}
        />
      </HeaderField>

      <HeaderField label="Controller model">
        <FieldValue loaded={loaded} value={car?.controllerModel} />
      </HeaderField>

      <HeaderField label="Drive type">
        <FieldValue loaded={loaded} value={car?.driveType} />
      </HeaderField>

      {/* `manufacturer`, not `oem`: the latter is the OEM's primary key, which the field
          rendered verbatim as a UUID. `CarSerializer` exposes the name alongside it. */}
      <HeaderField label="OEM">
        <FieldValue loaded={loaded} value={car?.manufacturer} />
      </HeaderField>

      {/* SSPI applies to every device type, so unlike CAT-5 there is no "does
          not apply" case — an empty one just hasn't been recorded. */}
      <HeaderField label="Last SSPI test">
        <FieldValue loaded={loaded} value={displayDate(car?.lastSspiTest)} fallback="N/A" />
      </HeaderField>

      <HeaderField label="Last CAT-1 test">
        <FieldValue loaded={loaded} value={displayDate(car?.lastCat1Test)} fallback="N/A" />
      </HeaderField>

      {/* Reads "Does not apply" on a non-traction lift rather than an empty
          date, which is a different thing to say than "not recorded". */}
      <HeaderField label="Last CAT-5 test">
        <FieldValue
          loaded={loaded}
          value={
            car
              ? formatLastCat5Test({
                  value: car.lastCat5Test ?? null,
                  nullValue: 'N/A',
                  elevatorType: car.elevatorType ?? null,
                })
              : null
          }
        />
      </HeaderField>
    </FieldRow>
  );
}
